/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { getResource } from '../../language/resources/getResource';
import { MediumText, UNSELECTABLE_TEXT } from '../../styles/styles';
import { DARK_GREY, RED_CREEK } from '../../styles/colors';
import { ellipsizeAtFirstLine } from '../guestCard/guestCardStyles';
import profileStub from '../../assets/images/profilePlaceholder.jpg';
import { PaymentMethodRegistration } from './paymentRegistration';
import xCircleImg from '../../assets/icons/xCircleRed.svg';
import { FADE_IN_KEYFRAMES } from '../../styles/animations';
import { getPaymentGuestDescription, PaymentGuest } from './paymentGuest';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../core/redux/IAppState';
import { getError, getLoading } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { showSnackbarAction } from '../../snackbar/snackbarAction';
import { deactivateGuestPaymentRegistrationAction } from './deactivateGuestPaymentRegistrationAction';
import { ApiError } from '../../core/redux/api/apiError';
import { clearApiErrorAction } from '../../core/redux/api/clearApiErrorAction';
import { BoxedErrorInverse } from '../../forms/elements/boxedMessage/boxedMessageStyles';
import ConfirmationModal from '../../forms/elements/ConfirmationModal';

const paymentRegistrationCss = css`
  min-width: 165px;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin: 5px;

  user-select: none;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
`;

const nameStyle =css`
  max-width: 165px;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 15px;
  margin-bottom: 10px;
  ${MediumText}; 
  color: ${DARK_GREY};
   
  ${ellipsizeAtFirstLine};
`;

const guestPhotoContainer = css`
  margin-top: 15px;
  border-radius: 100%; 
  width: 100px; 
  height: 100px; 
  overflow: hidden;
`;

const guestPhoto = css`
  width: 100%;
  height: 100%;
  ${UNSELECTABLE_TEXT};
`;

const deactivateIcon = css`
  width: 38px; 
  height: 38px; 
  pointer-events: initial;
  margin: auto;
  margin-bottom: -10px; /* align image with text */
`;

const deactivateButton = css`
  width: 130px;
  color: ${RED_CREEK};
  background-color: transparent;
  border: none;
  text-align: center;
  text-decoration: none;
  ${MediumText}
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;
  margin-bottom: 10px;
  &:hover {
    opacity: 0.75;
  }
}
`;

const deactivateButtonTextPosition = css`
  height: 50%;
  margin-left: 8px;
  margin-bottom: 10px;
  vertical-align: middle;
  display: inline-block;
`;


type Props = {
  guest: PaymentGuest;
  registration: PaymentMethodRegistration;
};


/**
 * PaymentRegistrationView: a component representing a payment registration related to a payment method
 * and allows the payment registration to be deactivated.
 */
export const PaymentRegistrationView = (props: Props) => {
    const deactivationRequested             = useRef(false);  // Need to update value between renders and do not intend to trigger a re-render
    const deactivationFailed                = useRef(false);
    const guestDisplayName                  = useRef(getPaymentGuestDescription(props.guest));
    const [paymentGuest, setPaymentGuest]   = useState<PaymentGuest>(props.guest);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

    const {
        resources,
        paymentGuests,
        isDeactivatingPaymentRegistration,
        paymentRegistrationDeactivateError,
    } = useSelector((state: IAppState) => ({
        resources:      state.locale,
        paymentGuests:  state.paymentGuests,
        isDeactivatingPaymentRegistration:      !!getLoading(state,  props.registration.registrationId.toString(), ACTION_TYPE.DEACTIVATE_GUEST_PAYMENT_REGISTRATION_REQUEST),
        paymentRegistrationDeactivateError:  getError(state,  props.registration.registrationId.toString(), ACTION_TYPE.DEACTIVATE_GUEST_PAYMENT_REGISTRATION_REQUEST),
    }));

    const dispatch = useDispatch();

    const deactivatePaymentRegistration = (registration: PaymentMethodRegistration) => {
        deactivationFailed.current = false;
        dispatch(deactivateGuestPaymentRegistrationAction(registration));
        dismissConfirmationModal();
    }

    const showSnackbar = useCallback((message: string) => dispatch(showSnackbarAction(message, false)), [dispatch]);
    const showError = useCallback((errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);
    const clearError = useCallback((error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType)), [dispatch]);   

    useEffect(() => {
        if (paymentGuests) {
            const index = paymentGuests.findIndex((pg) => pg.masterGuestId === props.registration?.masterGuestId);
            if (index >= 0) {   
                setPaymentGuest(paymentGuests[index]);
                guestDisplayName.current = getPaymentGuestDescription(paymentGuests[index]);
            }
        }
    },[paymentGuests, props.registration.masterGuestId]);

    useEffect(() => {
        if (paymentRegistrationDeactivateError) {
          clearError(paymentRegistrationDeactivateError);
          showError(getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION_ERROR'));
          deactivationFailed.current = true;
        }
    }, [paymentRegistrationDeactivateError, clearError, showError, resources]);
         
    useEffect(() => {
        if (isDeactivatingPaymentRegistration) {
            deactivationRequested.current = true;
        }
    },[isDeactivatingPaymentRegistration])


    // Called once on unmount
    useEffect(() => {
        return () => {      
            if (deactivationRequested.current && !deactivationFailed.current && !isDeactivatingPaymentRegistration && !paymentRegistrationDeactivateError) {
                showSnackbar(getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_SUCCEEDED', guestDisplayName.current));
            }
        }
    }, [isDeactivatingPaymentRegistration, paymentRegistrationDeactivateError, resources, showSnackbar]);

    const displayConfirmationModal = () => setShowConfirmationModal(true);
    const dismissConfirmationModal = () => setShowConfirmationModal(false);

return (
  <div css={paymentRegistrationCss}>
    {/* Photo */}
    <div css={guestPhotoContainer}>
        <img title={getPaymentGuestDescription(paymentGuest)}
            src={paymentGuest?.photoData || profileStub}
            alt='Guest'
            css={guestPhoto}/>
    </div>

    {/* First Name */}
    <span title={getResource(resources, 'PAYMENT_REGISTRATION_GUEST_FIRST_NAME')} css={nameStyle}>
                {paymentGuest?.firstName || getResource(resources, 'PAYMENT_REGISTRATION_GUEST_NO_FIRST_NAME')}
    </span>

    <button
        type="button"
        title={getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION')}
        onClick={displayConfirmationModal}
        css={deactivateButton}>
        <img css={deactivateIcon} src={xCircleImg} alt={'X'}
            title={getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION')}/>
        <span css={deactivateButtonTextPosition}>{getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION_BUTTON')}</span>
    </button>
    {
    showConfirmationModal
    && <ConfirmationModal
      rootCss={css`max-width: 700px;`}
      resources={resources}
      title={getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION')}
      icon={xCircleImg}
      message={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_CONFIRMATION', guestDisplayName.current)}
      styledMessage={getResource(resources, 'PAYMENT_REGISTRATION_WARNING')}
      style={BoxedErrorInverse}
      positiveButtonText={getResource(resources, 'DEACTIVATE_PAYMENT_REGISTRATION_BUTTON')}
      negativeButtonText={getResource(resources, 'PAYMENT_REGISTRATION_DEACTIVATE_CANCEL')}      
      onPositiveButtonClick={() => deactivatePaymentRegistration(props.registration)}
      onNegativeButtonClick={dismissConfirmationModal}
      onDismiss={dismissConfirmationModal}/>
  }
  </div>
)};
